import moment from 'moment';

import { isExpressTelehealth, isTelehealthBulkBilled } from './appointmentTypes';
import { VisitInfo } from 'src/services/auth';
import { AppointmentType } from 'src/services/availability';
import { BookingUserProfileModel } from 'src/services/booking';
import { MedicareCardStatus } from 'src/services/patient';

export enum ConflictType {
    none = 0,
    doctorNoNewPatient = 1,
    doctorOwnPatientsOnly = 2,
    lastVisitTooLongAgo = 3,
    outOfAgeRange = 4,
    patientBanned = 5,
}

export type ConflictResult = {
    valid: boolean;
    conflictId: ConflictType;
    reason: string;
};

export type ConflictRequest = {
    isNewPatient: boolean;
    patientDob: string;
    lastSeenDid?: number;
    lastF2FDate?: string;
    bannedLevel?: string;
    doctor?: BookingUserProfileModel;
    appointmentType?: AppointmentType;
};

// Perform Patient/Doctor/Appttype Conflic checking
export function PerformConflictChecks({
    appointmentType,
    bannedLevel,
    doctor,
    isNewPatient,
    lastF2FDate,
    lastSeenDid,
    patientDob,
}: ConflictRequest): ConflictResult {
    let conflict = { valid: true, conflictId: ConflictType.none, reason: '' };

    const lastF2FVisitInMonths =
        appointmentType?.lastF2FVisitInMonths !== undefined
            ? isTelehealthBulkBilled(appointmentType) && !isExpressTelehealth(appointmentType)
                ? NaN
                : parseInt(appointmentType.lastF2FVisitInMonths)
            : NaN;
    const lowAgeMin = appointmentType?.lowAgeMin ? parseInt(appointmentType.lowAgeMin) : NaN;
    const lowAgeMax = appointmentType?.lowAgeMax ? parseInt(appointmentType.lowAgeMax) : NaN;
    const upperAgeMin = appointmentType?.upperAgeMin ? parseInt(appointmentType.upperAgeMin) : NaN;
    const upperAgeMax = appointmentType?.upperAgeMax ? parseInt(appointmentType.upperAgeMax) : NaN;
    // Default values for age limits
    const minLow = lowAgeMin ?? 0;
    const maxLow = lowAgeMax ?? 200;
    const minHigh = upperAgeMin ?? 0;
    const maxHigh = upperAgeMax ?? 200;

    let patientAge = -1;
    try {
        patientAge = moment().diff(moment(patientDob.toString()), 'years');
    } catch (_) {
        patientAge = -1;
    }
    const noLimits =
        Number.isNaN(lowAgeMin) && Number.isNaN(lowAgeMax) && Number.isNaN(upperAgeMin) && Number.isNaN(upperAgeMax);
    const inLowRange =
        (!Number.isNaN(lowAgeMin) || !Number.isNaN(lowAgeMax)) && patientAge > minLow && patientAge < maxLow;
    const inUpperRange =
        (!Number.isNaN(upperAgeMin) || !Number.isNaN(upperAgeMax)) && patientAge > minHigh && patientAge < maxHigh;

    // 1: no new patient for Dr.
    if (isNewPatient && (doctor?.noNewPatient || doctor?.myPatient)) {
        conflict = {
            valid: false,
            conflictId: ConflictType.doctorNoNewPatient,
            reason: 'The selected doctor is not seeing new patients to the clinic.',
        };
    }
    // 2: dr will only see is own patients
    else if (doctor?.myPatient && (lastSeenDid === undefined || lastSeenDid.toString() !== doctor.bpUserId)) {
        conflict = {
            valid: false,
            conflictId: ConflictType.doctorOwnPatientsOnly,
            reason: 'The selected doctor does is not accepting any new patients.',
        };
    }
    // 3: last face-to-face visit too long ago
    else if (
        lastF2FVisitInMonths > 0 &&
        (!lastF2FDate ||
            (lastF2FDate &&
                moment().diff(moment(lastF2FDate.toString()).format('YYYY-MM-DD'), 'months') >= lastF2FVisitInMonths))
    ) {
        conflict = {
            valid: false,
            conflictId: ConflictType.lastVisitTooLongAgo,
            reason: 'Patients last Face to Face visit has been too long ago for this type of Appointment.',
        };
    }
    // 4: Appointment Type is not appropriate for Patient's Age
    else if (!(noLimits || inLowRange || inUpperRange)) {
        conflict = {
            valid: false,
            conflictId: ConflictType.outOfAgeRange,
            reason: 'Appointment Type is not appropriate for Patient Age.',
        };
    }
    // 5: Banned Patient - Note, banned patients will be caught as a patient verification exception error
    else if (bannedLevel && (bannedLevel.includes('Level 1') || bannedLevel.includes('Level 2'))) {
        conflict = { valid: false, conflictId: ConflictType.patientBanned, reason: 'Appointments can not be made.' };
    }
    return conflict;
}

export function requiresEligibilityCheck(
    appointmentType: AppointmentType,
    visitInfo: VisitInfo,
    medicareStatus: MedicareCardStatus
) {
    return (
        // is telehealth bulk billed
        isTelehealthBulkBilled(appointmentType) &&
        // valid medicare
        medicareStatus === MedicareCardStatus.Valid &&
        // no face-to-face within 12 months
        (!visitInfo.lastF2FDate ||
            moment().diff(moment(visitInfo.lastF2FDate.toString()).format('YYYY-MM-DD'), 'months') >= 12)
    );
}
